import { useState } from "react";
import { Check, Circle, Edit2, Trash2, Plus } from "react-feather";
import {
  selectShoppingListSummaries,
  selectActiveListId,
  selectPrimaryListId,
  selectCanCreateShoppingList,
  addShoppingList,
  renameShoppingList,
  setActiveShoppingList,
  deleteShoppingListAction,
  useT,
  shoppingListDisplayName,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";
import { useToast } from "./Toast";

// Switch, create, rename and delete shopping lists. Creating more than the one
// primary is premium-gated (selectCanCreateShoppingList).
export function ShoppingListSwitcher({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const lists = useAppSelector(selectShoppingListSummaries);
  const activeId = useAppSelector(selectActiveListId);
  const primaryId = useAppSelector(selectPrimaryListId);
  const canCreate = useAppSelector(selectCanCreateShoppingList);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState("");

  if (!open) return null;

  const switchTo = (id: string) => {
    dispatch(setActiveShoppingList(id));
    onClose();
  };

  const createList = () => {
    if (!canCreate) {
      toast.show(t("shopping.upgradeLists"));
      return;
    }
    const action = dispatch(addShoppingList());
    dispatch(setActiveShoppingList(action.payload.id));
    onClose();
  };

  const commitEdit = () => {
    if (editingId && nameDraft.trim()) {
      dispatch(renameShoppingList({ id: editingId, name: nameDraft.trim() }));
    }
    setEditingId(null);
  };

  const del = (id: string, name: string) => {
    if (!window.confirm(t("shopping.deleteListConfirmWeb", { name }))) return;
    if (id === activeId && primaryId) dispatch(setActiveShoppingList(primaryId));
    dispatch(deleteShoppingListAction(id));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-surface p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-ink mb-4">{t("shopping.listsTitle")}</h2>

        <div className="flex flex-col">
          {lists.map((l) =>
            editingId === l.id ? (
              <div key={l.id} className="flex items-center gap-2 py-1.5">
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitEdit();
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-terracotta"
                />
                <button
                  onClick={commitEdit}
                  className="rounded-lg bg-terracotta hover:bg-terracotta-dark transition-colors px-3 py-2 text-sm font-bold text-white cursor-pointer"
                >
                  {t("common.save")}
                </button>
              </div>
            ) : (
              <div key={l.id} className="flex items-center gap-1 group">
                <button
                  onClick={() => switchTo(l.id)}
                  className="flex-1 flex items-center gap-3 py-3 text-left cursor-pointer"
                >
                  {l.id === activeId ? (
                    <Check className="w-5 h-5 text-terracotta shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-ink-faint shrink-0" />
                  )}
                  <span
                    className={`flex-1 truncate text-base font-semibold ${
                      l.id === activeId ? "text-ink" : "text-ink-soft"
                    }`}
                  >
                    {shoppingListDisplayName(l.name, t)}
                  </span>
                  <span className="text-sm text-ink-faint">{l.count}</span>
                </button>
                <button
                  onClick={() => {
                    setEditingId(l.id);
                    setNameDraft(shoppingListDisplayName(l.name, t));
                  }}
                  aria-label={t("shopping.renameList", { name: shoppingListDisplayName(l.name, t) })}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-faint hover:text-ink-soft hover:bg-surface-2 cursor-pointer"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {!l.isPrimary && (
                  <button
                    onClick={() => del(l.id, l.name)}
                    aria-label={t("shopping.deleteListLabel", { name: l.name })}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-faint hover:text-red-600 hover:bg-surface-2 cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ),
          )}
        </div>

        <button
          onClick={createList}
          className="flex items-center gap-2 mt-3 py-2 text-terracotta hover:text-terracotta-dark font-bold cursor-pointer"
        >
          <Plus className="w-5 h-5" />
          {t("shopping.newList")}
        </button>
      </div>
    </div>
  );
}
